import { PUBLIC_MAGENTO_ENDPOINT } from '$env/static/public'
import { gt } from 'svelte-i18n-lingui'
import type { BaseProduct } from '$lib/js/elasticsearch/types'
import type { Address, AddressToSave, Country, CustomerInfo, Item, Order } from '../types'
import { getFullLocale, type SiteScope } from '$lib/js/helpers/i18n'
import { fetchJSON } from '$lib/js/helpers/http'


/**
 * Connect user to magento
 * @param {Object} user - The user credentials
 * @param {string} user.email - User's email
 * @param {string} user.password - User's password
 * @returns {Promise<{success: boolean, error?: string}>} A promise that resolves to an object with success status and optional error message
 */
export async function connectToMagento(user: { email: string; password: string }) {
	try {
		// we log out to prevent problems (for exemple apis /cart/mine does not work after a successful order in we don't logout)
		const logoutRequest = await fetch(
			`${PUBLIC_MAGENTO_ENDPOINT}/customer/account/logout/`, {
				method: "GET",
				credentials: 'include'
			}
		);
		
		// We fetch once the login of dev.bixoto.com to get the anonymous session cookie and the form key
		const loginPageRequest = await fetch(
			`${PUBLIC_MAGENTO_ENDPOINT}/customer/account/login/`, {
				method: "GET",
				credentials: 'include'
			}
		);
		
		const response = await loginPageRequest.text();
		const el = document.createElement('html');
		el.innerHTML = response;

		// We parse the response to get the form key
		const formKeyElement = el.querySelector('[name="form_key"]') as HTMLInputElement;
		
		if (!formKeyElement) {
			return { success: false, error: 'Form key not found in login page' };
		}
		
		const formKey = formKeyElement.value;

		const formData = new FormData();
		formData.append('form_key', formKey);
		formData.append('login[username]', user.email);
		formData.append('login[password]', user.password);

		// We perform a post request to properly login. This is mimicking the post request actually performed on the magento login page if you press enter.
		const loginRequest = await fetch(
			`${PUBLIC_MAGENTO_ENDPOINT}/customer/account/loginPost/`, {
				method: "POST",
				credentials: 'include',
				body: formData,
			}
		);
		
		// Check if login was successful
		if (loginRequest.ok) {
			return { success: true };
		} else {
			return { success: false, error: `Login failed with status: ${loginRequest.status}` };
		}
	} catch (error) {
		console.error('Error connecting to Magento:', error);
		return { success: false, error: error instanceof Error ? error.message : 'Unknown error occurred' };
	}
}

/**
 * Get information of customer with accessToken
 */
export async function getCustomerInfo(token: string) {
	return await fetchJSON<CustomerInfo>(`${PUBLIC_MAGENTO_ENDPOINT}/rest/V1/customers/me`, {
		headers: {
			Authorization: 'Bearer ' + token,
		},
	})
}

/**
 * Edit information of customer
 */

export async function editCustomerInfo(
	token: string,
	firstname: string,
	lastname: string,
	email: string,
) {
	const customer_info = await fetchJSON<CustomerInfo>(`${PUBLIC_MAGENTO_ENDPOINT}/rest/V1/customers/me`, {
		method: 'PUT',
		headers: {
			Authorization: 'Bearer ' + token,
		},
		json: {
			customer: {
				firstname: firstname,
				lastname: lastname,
				email: email,
			},
		},
	})
	return customer_info!
}

/**
 * Change password
 */

export async function changePassword(
	token: string,
	currentPassword: string,
	newPassword: string,
) {
	const ok = await fetchJSON<boolean>(`${PUBLIC_MAGENTO_ENDPOINT}/rest/V1/customers/me/password`, {
		method: 'PUT',
		headers: {
			Authorization: 'Bearer ' + token,
		},
		json: {
			currentPassword: currentPassword,
			newPassword: newPassword,
		},
	})
	return ok || false
}

/**
 * Subscribe to newsletter
 */

export async function subscribeNewsletter(token: string, customerInfo: CustomerInfo) {
	const newCustomerInfo = await fetchJSON<CustomerInfo>(`${PUBLIC_MAGENTO_ENDPOINT}/rest/V1/customers/me`, {
		method: 'PUT',
		headers: {
			Authorization: 'Bearer ' + token,
		},
		json: {
			customer: {
				firstname: customerInfo.firstname,
				lastname: customerInfo.lastname,
				email: customerInfo.email,
				extension_attributes: { is_subscribed: true },
			},
		},
	})
	return newCustomerInfo!
}

/**
 * Unsubscribe to newsletter
 */

export async function unsubscribeNewsletter(
	token: string,
	customerInfo: CustomerInfo,
) {
	const newCustomerInfo = await fetchJSON<CustomerInfo>(`${PUBLIC_MAGENTO_ENDPOINT}/rest/V1/customers/me`, {
		method: 'PUT',
		headers: {
			Authorization: 'Bearer ' + token,
		},
		json: {
			customer: {
				firstname: customerInfo.firstname,
				lastname: customerInfo.lastname,
				email: customerInfo.email,
				extension_attributes: { is_subscribed: false },
			},
		},
	})
	return newCustomerInfo!
}

/**
 * Add new address
 */

export async function addAddress(
	token: string,
	customerInfo: CustomerInfo,
	newAddress: AddressToSave,
) {
	const customer_info = await fetchJSON<CustomerInfo>(`${PUBLIC_MAGENTO_ENDPOINT}/rest/V1/customers/me`, {
		method: 'PUT',
		headers: {
			Authorization: 'Bearer ' + token,
		},
		json: {
			customer: {
				firstname: customerInfo.firstname,
				lastname: customerInfo.lastname,
				email: customerInfo.email,
				addresses: [...customerInfo.addresses, newAddress],
			},
		},
	})
	return customer_info!
}

/**
 * Edit address
 */

export async function editAddress(
	token: string,
	customerInfo: CustomerInfo,
	addressId: number,
	newAddress: AddressToSave,
) {
	const modifiedAddresses = customerInfo.addresses.map((address) =>
		address.id !== addressId ? address : { id: address.id, ...newAddress },
	)
	const newCustomerInfo = await fetchJSON<CustomerInfo>(`${PUBLIC_MAGENTO_ENDPOINT}/rest/V1/customers/me`, {
		method: 'PUT',
		headers: {
			Authorization: 'Bearer ' + token,
		},
		json: {
			customer: {
				firstname: customerInfo.firstname,
				lastname: customerInfo.lastname,
				email: customerInfo.email,
				addresses: modifiedAddresses,
			},
		},
	})

	return newCustomerInfo!
}

/**
 * delete address
 */
export async function deleteAddress(customerId: number, address: Address, options: { scope: SiteScope }) {
	const ok = await fetchJSON<boolean>(`/${options.scope}/endpoints/delete_address`, {
		method: 'POST',
		json: {
			customerId: customerId,
			addressCustomerId: address.customer_id,
			addressId: address.id,
		},
	})
	return ok || false
}

/**
 * Set address as default billing address
 */

export async function changeDefaultBilling(
	token: string,
	customerInfo: CustomerInfo,
	addressId: number,
	newAddress: AddressToSave,
	currentDefaultId: string,
) {
	const modifiedAddresses = customerInfo.addresses.map((address) => {
		if (address.id === addressId) {
			return { id: address.id, ...newAddress }
		} else if (address.id === parseInt(currentDefaultId)) {
			address.default_billing = false
			return address
		} else {
			return address
		}
	})
	const newCustomerInfo = await fetchJSON<CustomerInfo>(`${PUBLIC_MAGENTO_ENDPOINT}/rest/V1/customers/me`, {
		method: 'PUT',
		headers: {
			Authorization: 'Bearer ' + token,
		},
		body: JSON.stringify({
			customer: {
				firstname: customerInfo.firstname,
				lastname: customerInfo.lastname,
				email: customerInfo.email,
				addresses: modifiedAddresses,
			},
		}),
	})

	return newCustomerInfo!
}

/**
 * Set address as default shipping address
 */

export async function changeDefaultShipping(
	token: string,
	customerInfo: CustomerInfo,
	addressId: number,
	newAddress: AddressToSave,
	currentDefaultId: string,
) {
	const modifiedAddresses = customerInfo.addresses.map(address => {
		if (address.id === addressId) {
			return { id: address.id, ...newAddress }
		} else if (address.id === parseInt(currentDefaultId)) {
			address.default_shipping = false
			return address
		} else {
			return address
		}
	})
	const newCustomerInfo = await fetchJSON<CustomerInfo>(`${PUBLIC_MAGENTO_ENDPOINT}/rest/V1/customers/me`, {
		method: 'PUT',
		headers: {
			'Content-Type': 'application/json',
			Accept: 'application/json',
			Authorization: 'Bearer ' + token,
		},
		body: JSON.stringify({
			customer: {
				firstname: customerInfo.firstname,
				lastname: customerInfo.lastname,
				email: customerInfo.email,
				addresses: modifiedAddresses,
			},
		}),
	})

	return newCustomerInfo!
}

/**
 * Get orders of customer accessToken
 */

export async function getCustomerOrders(customerId: number, email: string, options: { scope: SiteScope }) {
	const orders = await fetchJSON<{ items: Order[] }>(`/${options.scope}/endpoints/all_orders`, {
		method: 'POST',
		json: {
			customerEmail: email,
			customerId: customerId,
		},
	})
	if (!orders) {
		return { items: [] }
	}

	return {
		items: orders.items?.map((order: Order) => {
			return { ...order, items: order?.items?.filter((item: Item) => item?.price_incl_tax > 0) ?? [] }
		}) ?? [],
	}
}

/**
 * Get one order of customer
 */
export async function getOrder({ orderId, incrementId, email, scope }: {
	orderId?: number,
	incrementId?: string,
	email: string | null | undefined,
	scope: SiteScope
}) {
	const params = new URLSearchParams({
		orderId: orderId ? '' + orderId : '',
		incrementId: incrementId ? '' + incrementId : '',
		email: email,
	} as Record<string, string>)

	return await fetchJSON<{
		order: Order,
		products: (BaseProduct | null)[],
		shipments: {
			items: any[] // TODO[typing]
		}
	}>(`/${scope}/endpoints/order?${params}`)
}

/**
 * Get the first product for each order to show them in the account.
 */
export async function getOrdersFirstProduct(orders: Order[], options: { scope: SiteScope }) {
	if (orders.length === 0) {
		return []
	}

	const skus = orders.map(order => order.items[0].sku)

	// Note: we use only .resized_thumbnail and .name of each product; we could reduce the fields we get
	const products = await fetchJSON<(BaseProduct | null)[]>(`/${options.scope}/endpoints/sku_products`, {
		method: 'POST',
		body: JSON.stringify(skus),
		headers: {
			'Content-Type': 'application/json',
		},
	})
	return products!
}

/**
 * Get one invoice ID for one order
 */
export async function getInvoiceId(order: Order, options: { scope: SiteScope }) {
	const payload = await fetchJSON<{
		invoice_id: number
	} | null>(`/${options.scope}/endpoints/invoice_id?orderId=${encodeURIComponent(order.entity_id)}`)
	return payload && payload.invoice_id
}


/**
/*   GET Countries and regions
/**/
export async function getCountries(_options: { scope: SiteScope }) {
	// TODO[i18n]: use the scope (do we need it for this route?)
	const countries = await fetchJSON<Country[]>(`${PUBLIC_MAGENTO_ENDPOINT}/rest/V1/directory/countries`)

	return countries || []
}

export const STATUS = {
	processing: gt`Processed`,
	closed: gt`Closed`,
	complete: gt`Complete`,
	canceled: gt`Canceled`,
	pending: gt`Payment pending validation`,
	awaiting_shipping: gt`In preparation`,
}

export const PAYMENT_METHODS = {
	paycomet_payment: gt`Credit or debit card`,
	banktransfer: gt`Bank transfer`,
	paycomet_bizum: 'Bizum',
	paypal_express: 'Paypal',
	stripe_payments: gt`Credit or debit card`,
	sequra_partpayments: gt`Split payment with Sequra`,
}

export function getCarrierTrackingURL(carrier: string, number: string, order: Order, { scope }: { scope: SiteScope }) {
	const postcode = order.extension_attributes.shipping_assignments[0].shipping.address.postcode

	switch (carrier.toLowerCase()) {
		case 'correos-express':
			return `https://s.correosexpress.com/search?shippingNumber=${number}`
		case 'chronopost':
			return `https://www.chronopost.fr/tracking-no-cms/suivi-page?listeNumerosLT=${number}&langue=${scope}`
		case 'correos':
			// TODO[i18n]: French link
			return `https://www.correos.es/es/es/herramientas/localizador/envios/detalle?tracking-number=${number}`
		case 'dachser':
			return `https://elogistics.dachser.com/shp2s/?javalocale=${getFullLocale(scope, '_')}&search=${number}`
		case 'db-schenker':
			return `https://www.dbschenker.com/app/tracking-public/?refNumber=${number}`
		case 'dhl':
			/* 			return `https://www.dhl.com/es-es/home/tracking/tracking-parcel.html?submit=1&tracking-id=${number}`
			 */
			// TODO[i18n]: French link
			return `https://clientesparcel.dhl.es/LiveTracking/ModificarEnvio/${number}`
		case 'gls':
			// TODO[i18n]: French link
			return `https://mygls.gls-spain.es/e/${number}/${postcode}/es`
		case 'seur':
			// TODO[i18n]: French link
			return `https://www.seur.com/livetracking/pages/seguimiento-online-busqueda.do?faces-redirect=true&segOnlineIdioma=es&segOnlineIdentificador=${number}`
		case 'ups':
		case 'united-parcel-service':
			// TODO[i18n]: French link
			return `https://wwwapps.ups.com/WebTracking/track?track=yes&trackNums=${number}&loc=${scope}_${scope}&requester=ST/trackdetails`
		case 'mrw':
			// TODO[i18n]: French link
			return 'https://www.mrw.es/seguimiento_envios/MRW_paqueteria_nacional_multiple.asp'
		case 'nacex':
			// TODO[i18n]: French link
			return 'https://www.nacex.es/irSeguimiento.do'
		case 'goi':
			// TODO[i18n]: French link
			return `https://chin.letsgoi.com/op/item-internacional-6/${number}`
		case 'ontime':
			// TODO[i18n]: French link
			return `https://ontimegts.alertran.net/gts/pub/clielocserv.seam?cliente=02801168&referencia=00${number}`
		case 'redur':
			return `https://redur.es/track-trace/?idioma=${scope}&buscarPor=NTRACKING&valor=${number}`
		case 'tipsa':
			// TODO[i18n]: French link
			return `https://aplicaciones.tip-sa.com/cliente/datos.php?id=${number}`
		case 'transloan':
			// TODO[i18n]: French link
			return `https://clientes.transloan.es/transloan/htracking.aspx?-2,${number}`
		case 'zeleris':
			// TODO[i18n]: French link
			return `https://www.zeleris.com/detalle-de-envio/?tracking_number=${number}`
		case 'pall-ex':
			// TODO[i18n]: French link
			return `https://mynexus.pallex.com/tracking/${number}/${postcode}`
		case 'tamdis':
			// TODO[i18n]: French link
			return `https://trackit.tamdis.com/${number}`
		case 'ctt':
			return `https://www.cttexpress.com/localizador-de-envios/?sc=${number}`
		default:
			return null
	}
}